// src/components/UpcomingSessions.jsx
// この先の授業（予定の回）と、休講・代講の設定【2026-09-28】
//
//   未来の回はDBに無いので、サーバーが曜日時限 × 学期の期間から予定を計算して返す
//   （GET /api/schedule/classes/:id/upcoming）。休講・代講は既存のAPIを使う。
//     休講: POST /api/schedule/sessions/cancel      { class_id, session_date, reason, notify }
//     代講: POST /api/schedule/sessions/substitute  { class_id, session_date, substitute_teacher_id, reason, notify }
//     取消: DELETE /api/schedule/sessions/:id/cancel
//   休講・代講は「その日」単位（同じ日に曜日時限が2つあれば両方）。
//   【2026-09-28】学期のはじめから出す（?scope=term）。
//     - 上に回数のまとめ（実施済み・あと何回・予定では何回）。目安は15回（週2曜日なら30、通年は×2）
//     - 各日に「第N回」。目安の回数までを出し、それより後は「続きを表示」
//     - 変な日の回（曜日時限に無い日・休業日・学期の外・時刻違い）に要確認の印
//     - カレンダー表示は使いたい人だけ（切り替えはこのブラウザに覚える）
import React, { useEffect, useState } from 'react';

const DAY = ['日', '月', '火', '水', '木', '金', '土'];
const STATE = {
  planned:    { label: '予定',     color: '#4b5563', bg: '#f3f4f6' },
  cancelled:  { label: '休講',     color: '#b91c1c', bg: '#fee2e2' },
  substitute: { label: '代講',     color: '#7c3aed', bg: '#ede9fe' },
  open:       { label: '受付中',   color: '#1d4ed8', bg: '#dbeafe' },
  held:       { label: '実施済み', color: '#15803d', bg: '#dcfce7' },
  closed:     { label: '休み',     color: '#6b7280', bg: '#e5e7eb' },   // 大学の授業カレンダー（休業・一部休講・振替）【2026-09-28】
  none:       { label: '記録なし', color: '#9a3412', bg: '#ffedd5' },   // 過ぎた日なのに回が無い【2026-09-28】
};
const VIEW_KEY = 'upcomingSessions.view';
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === 'calendar' ? 'calendar' : 'list'; } catch (e) { return 'list'; } };

export default function UpcomingSessions({ classId, auth, setErr, setMsg, reloadKey }) {
  const [data, setData]       = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [action, setAction]   = useState(null);   // { date, kind: 'cancel'|'substitute' }
  const [reason, setReason]   = useState('');
  const [notify, setNotify]   = useState(true);
  const [subId, setSubId]     = useState('');
  const [teachers, setTeachers] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [view, setViewState]  = useState(readView);
  const setView = (v) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch (e) { /* 覚えられなくても動く */ } };

  const load = async () => {
    try {
      const r = await fetch('/api/schedule/classes/' + classId + '/upcoming?scope=term', { headers: auth() });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || 'この先の授業を読み込めませんでした');
      setData(d);
    } catch (e) { setErr(e.message); setData({ items: [] }); }
  };
  useEffect(() => { load(); }, [classId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (date, kind) => {
    setAction({ date, kind }); setReason(''); setNotify(true); setSubId('');
    if (kind === 'substitute' && teachers === null) {
      try {
        const r = await fetch('/api/csv-import/teachers', { headers: auth() });
        const d = await r.json();
        setTeachers(d.teachers || []);
      } catch (e) { setTeachers([]); }
    }
  };

  const submit = async () => {
    const { date, kind } = action;
    if (kind === 'substitute' && !subId) { setErr('代講の先生を選んでください'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const body = { class_id: Number(classId), session_date: date, reason: reason.trim() || null, notify };
      if (kind === 'substitute') body.substitute_teacher_id = Number(subId);
      const r = await fetch('/api/schedule/sessions/' + (kind === 'cancel' ? 'cancel' : 'substitute'), {
        method: 'POST', headers: auth(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || '登録できませんでした');
      setMsg(d.message || (kind === 'cancel' ? `${date} を休講にしました` : `${date} を代講にしました`));
      setAction(null);
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const restore = async (it) => {
    if (!window.confirm(`${it.date} の${STATE[it.state].label}を取り消して、ふつうの授業に戻しますか？`)) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/schedule/sessions/' + it.sessionId + '/cancel', { method: 'DELETE', headers: auth() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.message || '取り消せませんでした');
      setMsg(d.message || `${it.date} をふつうの授業に戻しました`);
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="card"><p>読み込み中...</p></div>;
  const items = data.items || [];
  const sum = data.summary || null;
  const target = sum ? sum.target : 15;
  // 目安の回数までを出す（間の休講・休業の行や、要確認の行は含める）
  const cut = items.findIndex(it => it.no > target);
  const shown = showAll || cut < 0 ? items : items.slice(0, cut).concat(items.slice(cut).filter(it => it.flag));
  const hiddenCount = items.length - shown.length;
  const flagged = items.filter(it => it.flag);
  const md = (date) => { const [, m, d] = date.split('-'); return `${Number(m)}/${Number(d)}`; };
  const chip = (text, color, bg) => (
    <span style={{ background: bg, color, borderRadius: 999, padding: '0.1rem 0.6rem', fontSize: '0.85rem', fontWeight: 700 }}>{text}</span>
  );

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, flex: 1 }}>授業の回</h2>
        <div style={{ display: 'flex', gap: 0 }}>
          {[['list', '一覧'], ['calendar', 'カレンダー']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setView(v)} className={view === v ? 'btn-primary' : 'btn-secondary'}
                    style={{ padding: '0.15rem 0.6rem', fontSize: '0.8rem' }}>{l}</button>
          ))}
        </div>
      </div>

      {sum && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
          {chip(`実施済み ${sum.held}回`, '#15803d', '#dcfce7')}
          {chip(`あと ${Math.max(target - sum.held, 0)}回`, '#1d4ed8', '#dbeafe')}
          <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>
            （目安 {target}回 − 実施済み {sum.held}回）　予定では この先 {sum.planned}回
            {sum.cancelled > 0 && `・休講 ${sum.cancelled}回`}
            {sum.none > 0 && `・記録なし ${sum.none}日`}
          </span>
          {sum.held + sum.planned !== target && (
            <span style={{ fontSize: '0.85rem', color: '#b45309', fontWeight: 700 }}>
              {sum.held + sum.planned < target
                ? `予定どおりだと ${target - sum.held - sum.planned}回 足りません`
                : `予定どおりだと ${sum.held + sum.planned - target}回 多くなります`}
            </span>
          )}
        </div>
      )}
      {flagged.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.45rem 0.7rem',
                      marginBottom: '0.5rem', fontSize: '0.86rem', color: '#991b1b' }}>
          <strong>要確認 {flagged.length}件</strong>（ふだんの曜日時限と違う日の回です。補講ならそのままで大丈夫です）
          {flagged.map(it => (
            <div key={'f' + it.sessionId}>・{md(it.date)}（{DAY[it.dayOfWeek]}）{it.start || ''} … {it.flag}</div>
          ))}
        </div>
      )}
      <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 0 }}>
        曜日時限{data.termName ? `と学期（${data.termName}）` : ''}から計算しています
        {data.from && `（${md(data.from)}〜${md(data.to)}）`}。大学の授業カレンダー（休業日・振替）も反映。
        休講・代講はこの先の回から入れられます（その日の授業すべてが対象）。
      </p>
      {view === 'calendar' && items.length > 0 && <MonthGrids items={items} from={data.from} to={data.to} today={data.today} />}
      {view === 'list' && (items.length === 0 ? (
        <p style={{ color: '#888' }}>授業の回はありません（曜日時限と学期が未設定です）。</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>回</th><th>日付</th><th>時刻</th><th>教室</th><th>状態</th><th></th></tr></thead>
            <tbody>{shown.map((it, i) => {
              const st = STATE[it.state] || STATE.planned;
              const acting = action && action.date === it.date;
              const firstOfDay = shown.findIndex(x => x.date === it.date) === i;
              const past = data.today && it.date < data.today;
              return (
                <React.Fragment key={it.date + ':' + (it.scheduleId || it.sessionId)}>
                  {i > 0 && shown[i - 1].no && it.no && it.no - shown[i - 1].no > 1 && !showAll && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.78rem' }}>…</td></tr>
                  )}
                  <tr style={{
                    ...(it.state === 'cancelled' || it.state === 'closed' ? { color: '#9ca3af' } : past ? { color: '#6b7280' } : {}),
                    ...(it.flag ? { background: '#fef2f2' } : {}),
                  }}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 700, color: it.no > target ? '#b45309' : undefined }}>
                      {firstOfDay && it.no ? `第${it.no}回` : ''}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {md(it.date)}（{DAY[it.dayOfWeek]}）{it.extra && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}> 追加の回</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {it.start}{it.end ? `〜${it.end}` : ''}
                      {/* 実際に受付を開いた・終えた時刻【2026-10-05】曜日時限と同じなら出さない */}
                      {it.sessionStart && !it.extra && (it.sessionStart !== it.start || (it.sessionEnd && it.sessionEnd !== it.end)) && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>受付 {it.sessionStart}〜{it.sessionEnd || (it.state === 'open' ? '受付中' : '')}</div>
                      )}
                    </td>
                    <td>{it.room || <span style={{ color: '#9ca3af' }}>未設定</span>}</td>
                    <td>
                      <span style={{ background: st.bg, color: st.color, borderRadius: 999, padding: '0.05rem 0.5rem',
                                     fontSize: '0.78rem', fontWeight: 700 }}>{st.label}</span>
                      {it.state === 'substitute' && it.substituteName && <span style={{ marginLeft: '0.35rem', fontSize: '0.82rem' }}>{it.substituteName} 先生</span>}
                      {it.reason && <span style={{ marginLeft: '0.35rem', fontSize: '0.8rem', color: '#6b7280' }}>（{it.reason}）</span>}
                      {it.state === 'planned' && it.autoAttendance && <span style={{ marginLeft: '0.35rem', fontSize: '0.75rem', color: '#15803d' }}>自動で開始</span>}
                      {it.swappedFrom != null && <span style={{ marginLeft: '0.35rem', fontSize: '0.78rem', color: '#b45309', fontWeight: 700 }}>振替（{DAY[it.swappedFrom]}曜の授業）</span>}
                      {it.dayNote && <span style={{ marginLeft: '0.35rem', fontSize: '0.75rem', color: '#6b7280' }}>{it.dayNote}</span>}
                      {it.flag && <span style={{ marginLeft: '0.35rem', fontSize: '0.78rem', color: '#b91c1c', fontWeight: 700 }}>⚠ {it.flag}</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {firstOfDay && it.state === 'planned' && !past && !acting && (
                        <>
                          <button className="btn-secondary" disabled={busy} style={{ padding: '0.15rem 0.55rem', fontSize: '0.8rem', marginRight: '0.3rem' }}
                                  onClick={() => open(it.date, 'cancel')}>休講にする</button>
                          <button className="btn-secondary" disabled={busy} style={{ padding: '0.15rem 0.55rem', fontSize: '0.8rem' }}
                                  onClick={() => open(it.date, 'substitute')}>代講を頼む</button>
                        </>
                      )}
                      {firstOfDay && (it.state === 'cancelled' || it.state === 'substitute') && it.sessionId && (
                        <button className="btn-secondary" disabled={busy} style={{ padding: '0.15rem 0.55rem', fontSize: '0.8rem' }}
                                onClick={() => restore(it)}>取り消す</button>
                      )}
                    </td>
                  </tr>
                  {acting && firstOfDay && (
                    <tr>
                      <td colSpan={6} style={{ background: '#f8fafc' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
                          <strong style={{ alignSelf: 'center' }}>
                            {md(it.date)}（{DAY[it.dayOfWeek]}）を{action.kind === 'cancel' ? '休講にする' : '代講にする'}
                          </strong>
                          {action.kind === 'substitute' && (
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem' }}>代講の先生</label>
                              <select value={subId} onChange={e => setSubId(e.target.value)}>
                                <option value="">{teachers === null ? '読み込み中…' : '選んでください'}</option>
                                {(teachers || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem' }}>理由（任意・学生にも表示）</label>
                            <input value={reason} onChange={e => setReason(e.target.value)} style={{ width: 220 }}
                                   placeholder={action.kind === 'cancel' ? '例: 学会出張のため' : '例: 出張のため'} />
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', alignSelf: 'center', fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                            履修者に知らせる
                          </label>
                          <button className="btn-primary" disabled={busy} onClick={submit}>
                            {action.kind === 'cancel' ? '休講にする' : '代講にする'}
                          </button>
                          <button className="btn-secondary" disabled={busy} onClick={() => setAction(null)}>やめる</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}</tbody>
          </table>
        </div>
      ))}
      {view === 'list' && (hiddenCount > 0 || showAll) && cut >= 0 && (
        <button type="button" className="btn-secondary" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
                onClick={() => setShowAll(v => !v)}>
          {showAll ? `第${target}回までだけ表示` : `第${target + 1}回より後も表示（${hiddenCount}件）`}
        </button>
      )}
    </div>
  );
}

// カレンダー表示（使いたい人だけ）: 学期の月ごとに、授業の日へ「第N回」と状態の色を付ける
function MonthGrids({ items, from, to, today }) {
  const byDate = new Map();
  for (const it of items) { if (!byDate.has(it.date)) byDate.set(it.date, []); byDate.get(it.date).push(it); }
  const pad = (n) => String(n).padStart(2, '0');
  const ymdOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const dates = items.map(it => it.date).concat([from, to]).filter(Boolean).sort();
  const [fy, fm] = dates[0].split('-').map(Number);
  const [ty, tm] = dates[dates.length - 1].split('-').map(Number);
  const months = [];
  for (let y = fy, m = fm; y < ty || (y === ty && m <= tm); m === 12 ? (y++, m = 1) : m++) months.push([y, m]);
  const pick = (list) => list.find(x => x.flag) || list.find(x => x.no) || list[0];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '0.8rem', marginBottom: '0.5rem' }}>
      {months.map(([y, m]) => {
        const first = new Date(y, m - 1, 1);
        const cells = [];
        for (let i = 0; i < first.getDay(); i++) cells.push(null);
        for (let d = 1; d <= new Date(y, m, 0).getDate(); d++) cells.push(new Date(y, m - 1, d));
        return (
          <div key={y + '-' + m}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.2rem' }}>{y}年{m}月</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, fontSize: '0.72rem' }}>
              {DAY.map((w, i) => <div key={w} style={{ textAlign: 'center', color: i === 0 ? '#dc2626' : i === 6 ? '#2563eb' : '#6b7280' }}>{w}</div>)}
              {cells.map((d, i) => {
                if (!d) return <div key={'e' + i} />;
                const k = ymdOf(d);
                const list = byDate.get(k);
                const it = list && pick(list);
                const st = it ? (STATE[it.state] || STATE.planned) : null;
                const title = list ? list.map(x => `${x.no ? '第' + x.no + '回 ' : ''}${x.start || ''} ${(STATE[x.state] || STATE.planned).label}${x.flag ? ' ⚠' + x.flag : ''}${x.reason ? '（' + x.reason + '）' : ''}`).join('\n') : '';
                return (
                  <div key={k} title={title} style={{
                    minHeight: 34, borderRadius: 4, padding: '1px 2px', textAlign: 'center',
                    background: st ? st.bg : 'transparent', color: st ? st.color : '#9ca3af',
                    border: it && it.flag ? '2px solid #dc2626' : k === today ? '2px solid #111827' : '1px solid #f3f4f6',
                  }}>
                    <div>{d.getDate()}</div>
                    {it && <div style={{ fontWeight: 700 }}>{it.flag ? '⚠' : it.no ? it.no : st.label.slice(0, 2)}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
        {Object.values(STATE).map(v => (
          <span key={v.label}><span style={{ display: 'inline-block', width: 10, height: 10, background: v.bg, border: `1px solid ${v.color}`, marginRight: 3 }} />{v.label}</span>
        ))}
        <span>数字＝第N回　<span style={{ color: '#dc2626' }}>⚠＝要確認</span>　太枠＝今日</span>
      </div>
    </div>
  );
}
